import { ReactNode, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import {
  Base,
  LockIcon,
  WarningCircleIcon,
  toast
} from '@itsrever/design-system'
import { Layered, LayeredActions } from '@/domain/group-settings'
import { useDialog } from '@/ui/components/dialog/dialog-provider'
import { SaveBar } from '@/ui/shared'
import { equalJSON } from '@/utils'
import { WarehousesScope } from '@/hooks/use-warehouses-scope'

const { Badge, Button, Callout } = Base

export type Scope = Exclude<WarehousesScope, { kind: 'none' }>

export interface FormProps<T> {
  value: T
  saved: T
  onChange: (value: T) => void
  readOnly: boolean
}

export interface LayeredSettingsProps<T extends object> {
  scope: Scope
  layered: Layered<T>
  // What a group without these settings starts from.
  defaults: T
  actions: LayeredActions<T>
  render: (props: FormProps<T>) => ReactNode
  // Asks before saving a change that needs it; false keeps editing.
  confirmSave?: (draft: T, saved: T) => Promise<boolean>
  // Saves what the draft keeps outside these settings, before them.
  saveAlso?: (draft: T, saved: T) => Promise<void>
  saveAlsoError?: string
  // What blocks saving the draft, e.g. a field out of range.
  problem?: (draft: T) => string | undefined
  onShowGroup: () => void
  onShowStore: (ecommerceId: string) => void
}

// One kind of settings for the group, or for one portal of it, like the warehouse routing rules.
export function LayeredSettings<T extends object>(
  props: LayeredSettingsProps<T>
) {
  return props.scope.kind === 'group' ? (
    // Keyed by the stored settings so a save or a refetch replaces the draft it made obsolete.
    <GroupSettings key={JSON.stringify(props.layered.group)} {...props} />
  ) : (
    // Keyed by the store and what it gets, so a store switch, a save or a refetch replaces an obsolete draft.
    <StoreSettings
      key={`${props.scope.ecommerceId}:${JSON.stringify(props.layered.overrides[props.scope.ecommerceId] ?? props.layered.group)}:${JSON.stringify(props.layered.portals?.[props.scope.ecommerceId])}`}
      {...props}
      ecommerceId={props.scope.ecommerceId}
    />
  )
}

const countChanges = <T extends object>(draft: T, saved: T) =>
  Object.keys(draft).filter(
    (k) => !equalJSON(draft[k as keyof T], saved[k as keyof T])
  ).length

function GroupSettings<T extends object>({
  scope,
  layered,
  defaults,
  actions,
  render,
  confirmSave,
  saveAlso,
  saveAlsoError,
  problem,
  onShowStore
}: LayeredSettingsProps<T>) {
  const { t } = useTranslation()
  const saved = layered.group ?? defaults
  const [draft, setDraft] = useState(saved)
  const showDialog = useDialog()
  const inheriting = scope.stores.filter(
    (store) => !layered.overrides[store]
  ).length
  const overriding = scope.stores.some((store) => layered.overrides[store])
  const changes = countChanges(draft, saved)

  const resetStore = async (store: string) => {
    const confirmed = await showDialog({
      title: t('group_settings.reset_confirm_title'),
      message: (
        <Trans
          i18nKey="group_settings.reset_confirm_message"
          values={{ store, group: scope.group, count: inheriting }}
          components={{ b: <b /> }}
        />
      ),
      okButtonText: t('group_settings.reset_confirm_ok'),
      cancelButtonText: t('group_settings.cancel'),
      destructive: true
    })
    if (confirmed) actions.resetStore(store)
  }

  return (
    <div className="flex flex-col gap-3">
      <Callout tone="success" className="text-xs leading-relaxed">
        <Trans
          i18nKey="group_settings.group_banner"
          values={{ group: scope.group }}
          components={{ b: <b /> }}
        />
      </Callout>
      {render({ value: draft, saved, onChange: setDraft, readOnly: false })}
      <Base.Card variant="flat" className="flex flex-col gap-3 p-5">
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-neutral-900">
            {t('group_settings.portals_title', { group: scope.group })}
          </span>
          {overriding ? (
            <span className="text-xs text-neutral-600">
              {t('group_settings.portals_hint')}
            </span>
          ) : null}
        </div>
        {/* Rows share the columns through subgrid, so the badges line up whatever actions each row has. */}
        <div className="grid grid-cols-[1fr_1fr_auto] gap-x-3 rounded-lg border border-solid border-neutral-100">
          {scope.stores.map((store) => {
            const override = layered.overrides[store]
            return (
              <div
                key={store}
                className="col-span-3 grid grid-cols-subgrid items-center border-b border-solid border-neutral-100 px-3 py-2 text-xs last:border-b-0"
              >
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="justify-self-start"
                  onClick={() => onShowStore(store)}
                >
                  {store}
                </Button>
                <span className="justify-self-start">
                  {override ? (
                    <Badge tone="warning">
                      {t('group_settings.own_settings')}
                    </Badge>
                  ) : (
                    <Badge tone="success">
                      {t('group_settings.follows_group')}
                    </Badge>
                  )}
                </span>
                {override ? (
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label={t('group_settings.edit_store_aria', {
                        store
                      })}
                      onClick={() => onShowStore(store)}
                    >
                      {t('group_settings.edit_store')}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label={t('group_settings.reset_store_aria', {
                        store
                      })}
                      onClick={() => resetStore(store)}
                    >
                      {t('group_settings.reset_to_group')}
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={t('group_settings.override_store_aria', {
                      store
                    })}
                    onClick={() => onShowStore(store)}
                  >
                    {t('group_settings.override_store')}
                  </Button>
                )}
              </div>
            )
          })}
        </div>
      </Base.Card>
      {changes ? (
        <SaveBar
          changes={changes}
          problem={problem?.(draft)}
          saveLabel={t('group_settings.save_for', { count: inheriting })}
          saving={actions.savingGroup}
          onDiscard={() => setDraft(saved)}
          onSave={async () => {
            if (confirmSave && !(await confirmSave(draft, saved))) return
            if (
              saveAlso &&
              !(await savedAlso(saveAlso, draft, saved, saveAlsoError))
            )
              return
            actions.saveGroup(draft)
          }}
        />
      ) : null}
    </div>
  )
}

function StoreSettings<T extends object>({
  scope,
  ecommerceId,
  layered,
  defaults,
  actions,
  render,
  confirmSave,
  saveAlso,
  saveAlsoError,
  problem,
  onShowGroup
}: LayeredSettingsProps<T> & { ecommerceId: string }) {
  const { t } = useTranslation()
  const showDialog = useDialog()
  const group = {
    ...(layered.group ?? defaults),
    ...layered.portals?.[ecommerceId]
  }
  const override = layered.overrides[ecommerceId]
  const [unlocked, setUnlocked] = useState(false)
  const [draft, setDraft] = useState(() => override ?? group)
  const others = scope.stores.filter(
    (s) => s !== ecommerceId && !layered.overrides[s]
  ).length
  const state = override ? 'overridden' : unlocked ? 'editing' : 'inherited'
  // An inherited store shows the group's settings as they are now, not as they were on mount.
  const value = state === 'inherited' ? group : draft
  // Until it is saved, an override is measured against the group settings it starts from.
  const saved = override ?? group
  const groupLink = (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      inline
      onClick={onShowGroup}
    />
  )
  const values = { store: ecommerceId, group: scope.group, count: others }
  const save = async () => {
    if (confirmSave && !(await confirmSave(draft, saved))) return
    if (saveAlso && !(await savedAlso(saveAlso, draft, saved, saveAlsoError)))
      return
    actions.saveStore(ecommerceId, draft)
  }

  const reset = async () => {
    const confirmed = await showDialog({
      title: t('group_settings.reset_confirm_title'),
      message: (
        <Trans
          i18nKey="group_settings.reset_confirm_message"
          values={values}
          components={{ b: <b /> }}
        />
      ),
      okButtonText: t('group_settings.reset_confirm_ok'),
      cancelButtonText: t('group_settings.cancel'),
      destructive: true
    })
    if (!confirmed) return
    actions.resetStore(ecommerceId, {
      onSuccess: () => {
        setUnlocked(false)
        setDraft(group)
      }
    })
  }

  return (
    <div className="flex flex-col gap-3">
      {state === 'inherited' ? (
        <Callout
          tone="info"
          icon={<LockIcon />}
          className="text-xs leading-relaxed"
        >
          <Trans
            i18nKey="group_settings.inherited_banner"
            values={values}
            components={{ b: <b />, action: groupLink }}
          />
        </Callout>
      ) : state === 'editing' ? (
        <Callout
          tone="warning"
          icon={<WarningCircleIcon />}
          className="text-xs leading-relaxed"
        >
          <Trans
            i18nKey="group_settings.editing_banner"
            values={values}
            components={{ b: <b />, action: groupLink }}
          />
        </Callout>
      ) : (
        <Callout tone="warning" className="text-xs leading-relaxed">
          <Trans
            i18nKey="group_settings.overridden_banner"
            values={values}
            components={{ b: <b />, action: groupLink }}
          />
        </Callout>
      )}
      {render({
        value,
        saved,
        onChange: setDraft,
        readOnly: state === 'inherited'
      })}
      {state === 'inherited' ? (
        <Footer>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setUnlocked(true)}
          >
            <LockIcon className="size-3" />
            {t('group_settings.unlock')}
          </Button>
        </Footer>
      ) : state === 'overridden' ? (
        <Footer>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            loading={actions.resettingStore}
            onClick={reset}
          >
            {t('group_settings.reset_store')}
          </Button>
        </Footer>
      ) : null}
      {state === 'editing' ? (
        // Unlocked but unsaved is a pending override even before any edit.
        <SaveBar
          pending
          changes={countChanges(draft, saved)}
          problem={problem?.(draft)}
          discardLabel={t('group_settings.cancel')}
          saveLabel={t('group_settings.save_store')}
          saving={actions.savingStore}
          onDiscard={() => {
            setUnlocked(false)
            setDraft(group)
          }}
          onSave={save}
        />
      ) : state === 'overridden' && countChanges(draft, saved) ? (
        <SaveBar
          changes={countChanges(draft, saved)}
          problem={problem?.(draft)}
          saving={actions.savingStore}
          onDiscard={() => setDraft(saved)}
          onSave={save}
        />
      ) : null}
    </div>
  )
}

// A failed save of the rest stops the save, so the settings are never saved by halves.
async function savedAlso<T>(
  saveAlso: (draft: T, saved: T) => Promise<void>,
  draft: T,
  saved: T,
  error: string | undefined
) {
  try {
    await saveAlso(draft, saved)
    return true
  } catch (e) {
    console.warn(e)
    if (error) toast({ text: error, variant: 'error' })
    return false
  }
}

const Footer = ({ children }: { children: ReactNode }) => (
  <div className="flex items-center justify-end gap-2">{children}</div>
)
